import { Contact, ContactStatus } from '@wonderschool/common-base-types';
import { Button, RefreshIcon, Send2Icon, Tooltip } from '@wonderschool/common-base-ui';
import { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';
import { WsSpinner, WsWidgetSizeEnum } from '../../common';
import {
  SendInvitationConfirmationDialog,
  showInvitationErrorToast,
  showInvitationSuccessToast,
  useContacts,
} from '../../contacts';
import { inviteContact, isInvitableContact } from '../../contacts/contactsAPI';
import { toDateObject } from '../../helpers/dates';
import { logError } from '../../rollbar';

interface Invitation {
  invitationId: string;
  email: string;
  sentAt: string;
  displayName: string;
}

export function InviteStatus(familyRow: any, t: any) {
  const [allInvitations, setAllInvitations] = useState<Invitation[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [sendToContact, setSendToContact] = useState(null);
  const { contacts } = useContacts();

  const fetchAllInvitations = async () => {
    const invitations = contacts.map((invitation: Invitation) => ({
      invitationId: invitation.invitationId,
      email: invitation.email,
      sentAt: invitation.sentAt,
      displayName: invitation.displayName,
    }));
    setAllInvitations(invitations);
  };

  useEffect(() => {
    fetchAllInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyRow]);

  async function onSendInvitation(contact: Contact) {
    try {
      setLoader(true);
      contact.status = ContactStatus.inviting;
      invariant(isInvitableContact(contact), 'There was an error inviting the contact');
      await inviteContact(contact);
      showInvitationSuccessToast(contact);
    } catch (error) {
      setLoader(false);
      logError('Error inviting contact', error, 'InviteStatus');
      showInvitationErrorToast(contact, error);
    } finally {
      setSendToContact(null);
      setLoader(false);
    }
    return null;
  }

  const handleClickSend = (family: Contact) => {
    const findContact = contacts.find((obj: Contact) => obj.email === family.email || obj.id === family.id);
    setSendToContact(findContact);
  };

  function calculateInvitationTimeDifference(member: Contact) {
    if (!allInvitations) return '';
    const invitation = allInvitations.find(
      (invitation: Invitation) => invitation.email === member.email && invitation.displayName === member.displayName
    );

    if (!invitation || !invitation.sentAt) return '';

    const invitationSentDate = toDateObject(invitation.sentAt, 'MM/DD/YYYY');
    if (!invitationSentDate) return ''; // Add null check for invitationSentDate

    const timeDifference = Math.round((new Date().valueOf() - invitationSentDate.valueOf()) / (1000 * 3600 * 24));
    if (timeDifference > 0) {
      return t('familyDirectory.invitation.daysAgo', { count: timeDifference });
    } else {
      const hoursDifference = Math.round((new Date().valueOf() - invitationSentDate.valueOf()) / (1000 * 3600));
      if (hoursDifference === 0) return t('familyDirectory.invitation.justNow');
      return t('familyDirectory.invitation.hoursAgo', { count: hoursDifference });
    }
  }

  function displayButton(member: Contact) {
    const isEmailEmpty = !!member.email;
    const button = (
      <Button
        primary={isEmailEmpty && member.status === ContactStatus.new}
        disabled={!isEmailEmpty}
        data-testid="parents-invite-btn"
        extraClasses="w-full w-36 min-h-58 min-h-10"
        onClick={() => handleClickSend(member)}
        preIcon={
          member.status === ContactStatus.invited ? (
            <Send2Icon className="h-5 w-5 font-normal text-blue-800" />
          ) : (
            <RefreshIcon className="h-5 w-5 font-normal text-white" />
          )
        }
        label={
          member.status === ContactStatus.invited
            ? t('familyDirectory.invitation.resendAction')
            : t('familyDirectory.invitation.sendAction')
        }
      />
    );

    return !isEmailEmpty ? (
      <Tooltip content={t('familyDirectory.invitation.emailRequiredTooltip')} action="hover">
        <div className="min-h-58 min-h-10 w-full max-w-36 py-1">{button}</div>
      </Tooltip>
    ) : (
      button
    );
  }

  return (
    <>
      <SendInvitationConfirmationDialog
        onClose={() => setSendToContact(null)}
        onYes={onSendInvitation}
        contact={sendToContact}
      />
      <div
        className={`min-h-12 content-center ${familyRow.status === ContactStatus.inviting ? 'max-w-36 text-center' : 'm-auto'}`}
      >
        {familyRow.status === ContactStatus.inviting && (
          <Button extraClasses="max-w-36 min-h-10 w-full" preIcon={<WsSpinner size={WsWidgetSizeEnum.SMALL} />}>
            {t('parentsListing.sending')}
          </Button>
        )}
        {familyRow.status === ContactStatus.invited || familyRow.status === ContactStatus.new ? (
          <>
            {displayButton(familyRow)}
            <div className="w-full max-w-36 text-center text-xs">
              {loader && familyRow.status === ContactStatus.invited ? (
                <Button extraClasses="max-36 w-full text-center" />
              ) : (
                calculateInvitationTimeDifference(familyRow)
              )}
            </div>
          </>
        ) : (
          familyRow.status === ContactStatus.verified && (
            <p className="max-w-36 text-center text-gray-800">{t('parentsListing.inviteAccepted')}</p>
          )
        )}
      </div>
    </>
  );
}
